import AddIcon from '@mui/icons-material/Add';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteButton from '../components/DeleteButton';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Grid, IconButton, InputAdornment, TextField, Toolbar } from '@mui/material';
import * as React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';

import { DataAPIContext, GET_ONE } from '../api';
import OutlinedExportButton from '../components/OutlinedExportButton';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { ContextStore } from '../store/contextStore';
import { debounce, isOk } from '../utils';

export const TagListToolbar = (props) => {
    const history = useHistory();
    const searchQueryRef = React.useRef(null);
    // const classes = useToolbarStyles();
    const btSize = props.isSmallScreen ? 'medium' : 'small';
    const { hasPerm } = React.useContext(ContextStore);
    const dataAPI = React.useContext(DataAPIContext);

    const handleSearchQueryChange = () => {
        debouncedSearchQueryChangeHandler.cancel();
        doSearch(searchQueryRef.current?.value);
    };

    const onCreateNewItemBtClick = () => {
        history.push('/tags/create');
    };

    const onDeleteItemBtClick = () => {
        props.onDeleteItem && props.onDeleteItem(props.selectedIds);
    };

    const debouncedSearchQueryChangeHandler = debounce(handleSearchQueryChange, 300);

    const doSearch = (query) => props.onQueryChange && props.onQueryChange(query);
    const handleCategoryFilterChange = (_ev, selectedCategory) => {
        setSelectedCategoryObject(selectedCategory);
        if (selectedCategory) {
            props.setTagCategoryListFilter(selectedCategory.id);
        } else {
            props.setTagCategoryListFilter(null);
        }
        props.setLastUpdate(new Date().toString());
        props.resetPagination && props.resetPagination();
    };

    const handleClearFilter = () => {
        setSelectedCategoryObject(null);
        props.setTagCategoryListFilter(null);
        props.setLastUpdate(new Date().toString());
        props.resetPagination && props.resetPagination();
    };

    // Convert ID back to object for RemoteAutocomplete value
    const [selectedCategoryObject, setSelectedCategoryObject] = React.useState(null);

    React.useEffect(() => {
        const id = props.tagCategoryListFilter;
        if (!id) {
            setSelectedCategoryObject(null);
        } else if (!selectedCategoryObject || String(selectedCategoryObject.id) !== String(id)) {
            // An id without its option (a /tags?category=<id> link, back or forward): named below.
            setSelectedCategoryObject({ id });
        }
    }, [props.tagCategoryListFilter, selectedCategoryObject]);

    // The options are one page, which need not hold that category: ask for it. When that fails (an unknown id, or
    // another company's: both 404; a refusal; no answer), the filter and the URL's parameter go: never an unnamed
    // filter over an empty list.
    React.useEffect(() => {
        const id = selectedCategoryObject?.id;
        if (id == null || selectedCategoryObject.name != null) {
            return undefined;
        }
        let active = true;
        const drop = () => {
            if (active) {
                handleClearFilter();
            }
        };
        dataAPI(GET_ONE, 'tour_tag_category', { id, keepSessionOn403: true })
            .then((res) => {
                if (!active) {
                    return;
                }
                if (isOk(res) && res.data && String(res.data.id) === String(id) && res.data.name != null) {
                    setSelectedCategoryObject(res.data);
                } else {
                    drop();
                }
            })
            .catch(drop);
        return () => { active = false; };
        // handleClearFilter is new on every render (so are the props it calls): only the shown category re-asks.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataAPI, selectedCategoryObject]);
    return (
        <Box sx={{ mt: { xs: 1, sm: 0 } }}>
            <Toolbar disableGutters={true}>
                <Box sx={{ width: '100%' }}>
                    <Grid container spacing={{ xs: 1, md: 2 }} sx={{ width: '100%' }}>
                        <Grid item xs={12} md={9}>
                            <Grid container spacing={{ xs: 1, md: 2 }} alignItems="center">
                                <Grid item xs sm md={4}>
                                    <TextField
                                        variant="filled"
                                        label="Search"
                                        size="small"
                                        onChange={debouncedSearchQueryChangeHandler}
                                        inputRef={searchQueryRef}
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton aria-label="do search" onClick={handleSearchQueryChange} size={btSize}>
                                                        <SearchIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                {/* Same [+] and icon-only Export as Activities on a phone (CP-5563, CP-8289). */}
                                <Grid item xs="auto" sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-end', gap: 1 }}>
                                    {/* Read-only tag categories with their schema (metaplan A10). */}
                                    <Button onClick={() => history.push('/tags/categories')} variant="outlined" color="primary" aria-label="Tag categories">
                                        <CategoryOutlinedIcon />
                                    </Button>
                                    {hasPerm('tag', 'write') && (
                                        <Button onClick={onCreateNewItemBtClick} variant="contained" color="primary">
                                            <AddIcon />
                                        </Button>
                                    )}
                                    {hasPerm('tag', 'read') && (
                                        <OutlinedExportButton
                                            onClick={props.onExportList}
                                            size={btSize}
                                            hideLabel
                                            tooltip="Export list as XLSX"
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={9} sm={10} md={4}>
                                    <RemoteAutocomplete
                                        resource="tour_tag_category"
                                        label="Tag Category"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        onChange={handleCategoryFilterChange}
                                        value={selectedCategoryObject}
                                        getOptionLabel={(opt) => opt?.name || ''}
                                        textFieldProps={{
                                            placeholder: "Select category to filter..."
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2} md="auto" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    {props.tagCategoryListFilter && (
                                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                size={btSize}
                                                onClick={handleClearFilter}
                                                sx={{ p: 1, minWidth: 0 }}
                                            >
                                                <ClearIcon />
                                            </Button>
                                        </Box>
                                    )}
                                    {props.tagCategoryListFilter && (
                                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                size={btSize}
                                                onClick={handleClearFilter}
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                <ClearIcon />&nbsp;Clear Tag
                                            </Button>
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={3} sx={{ mt: { xs: 0, md: 1 }, display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                            <Button
                                onClick={() => history.push('/tags/categories')}
                                variant="outlined"
                                color="primary"
                                size={btSize}
                                sx={{ whiteSpace: 'nowrap', mr: 1 }}
                            >
                                <CategoryOutlinedIcon />&nbsp;Categories
                            </Button>
                            {props.selectedIds?.length > 0 && !props.isSmallScreen && (
                                <DeleteButton perm="tag.delete" onClick={onDeleteItemBtClick} size={btSize} sx={{ whiteSpace: 'nowrap' }}>
                                    Delete {props.selectedIds.length > 1 ? 'tags' : 'tag'}
                                </DeleteButton>
                            )}
                            {hasPerm('tag', 'write') && props.selectedIds?.length === 0 && (
                                <>
                                    <Button
                                        onClick={onCreateNewItemBtClick}
                                        variant="contained"
                                        color="primary"
                                        size={btSize}
                                        sx={{ whiteSpace: 'nowrap', mr: 1 }}
                                    >
                                        <AddIcon />&nbsp;New tag
                                    </Button>
                                    {hasPerm('tag', 'read') && (
                                        <OutlinedExportButton
                                            onClick={props.onExportList}
                                            size={btSize}
                                            tooltip="Export list as XLSX"
                                        />
                                    )}
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Box>
            </Toolbar>
        </Box>
    );
};

export default TagListToolbar;

TagListToolbar.propTypes = {
    isSmallScreen: PropTypes.bool,
    onDeleteItem: PropTypes.func,
    selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    onQueryChange: PropTypes.func,
    setTagCategoryListFilter: PropTypes.func.isRequired,
    setLastUpdate: PropTypes.func.isRequired,
    resetPagination: PropTypes.func,
    tagCategoryListFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

TagListToolbar.defaultProps = {
    isSmallScreen: false,
    selectedIds: [],
};
